import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Box, AlertCircle, Eye, Filter, Calendar, Hash, List } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/integrations/supabase/client';
import Interactive3DViewer from './Interactive3DViewer';
import MeasurementPopup from './MeasurementPopup';
import DistanceMeasurementPopup from './DistanceMeasurementPopup';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getCurrentTenantSlug } from '@/utils/tenant-utils';
import { WorkspaceService } from '@/services/workspace-service';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);

interface ManufacturingProcess {
  id: string;
  name: string;
  description?: string;
}

interface ManualConfig {
  id: string;
  form_title: string;
  data_type: string;
  config_data: {
    part_configuration_id?: string;
    parsing_configuration_id?: string;
  };
}

interface STLViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  station: ManufacturingProcess | null;
  formTitle: string;
  configId: string;
  manualConfigs: ManualConfig[];
}

interface MarkerPosition {
  id: string;
  featureLabel: string;
  position: [number, number, number];
}

interface MeasurementData {
  feature_label: string;
  characteristic: string;
  actual_value: number;
  deviation?: number;
  nominal_value?: number;
  upper_tolerance?: number;
  lower_tolerance?: number;
  sample_date: string;
  sample_id: string;
  metadata?: any; // Add this line
}

// Utility: parse sample date/time using format from metadata
function getSampleDateObj(sample: any) {
  const dateStr = sample.metadata?.date;
  const timeStr = sample.metadata?.time;
  const dateFormat = sample.metadata?.date_format || 'DD/MM/YYYY';
  const timeFormat = sample.metadata?.time_format || 'HH:mm:ss';
  

  
  if (dateStr && timeStr) {
    // Try parsing with the exact format first
    let result = dayjs(`${dateStr} ${timeStr}`, `${dateFormat} ${timeFormat}`);
    
    // If that fails, try with more flexible parsing
    if (!result.isValid()) {
      
      // Try different date formats that are more flexible
      const flexibleDateFormats = [
        'DD/MM/YYYY',
        'D/M/YYYY', 
        'DD/MM/YY',
        'D/M/YY',
        'MM/DD/YYYY',
        'M/D/YYYY',
        'MM/DD/YY',
        'M/D/YY'
      ];
      
      let dateResult = null;
      let timeResult = null;
      
      // Try to parse date with flexible formats
      for (const format of flexibleDateFormats) {
        dateResult = dayjs(dateStr, format);
        if (dateResult.isValid()) {
          break;
        }
      }
      
      // Try to parse time
      timeResult = dayjs(timeStr, timeFormat);
      if (!timeResult.isValid()) {
        // Try alternative time formats
        const timeFormats = ['HH:mm:ss', 'HH:mm', 'H:mm:ss', 'H:mm'];
        for (const format of timeFormats) {
          timeResult = dayjs(timeStr, format);
          if (timeResult.isValid()) {
            break;
          }
        }
      }
      
      if (dateResult && dateResult.isValid() && timeResult && timeResult.isValid()) {
        // Combine the valid date and time
        result = dateResult.hour(timeResult.hour()).minute(timeResult.minute()).second(timeResult.second());
      }
    }
    return result;
  } else if (dateStr) {
    const result = dayjs(dateStr, dateFormat);
    return result;
  } else if (sample.sample_date) {
    const result = dayjs(sample.sample_date);
    return result;
  } else {
    return dayjs('invalid'); // create an invalid date
  }
}

// Utility: get sorted samples oldest to newest
function getChronologicalSamples(samples: any[]) {
  return [...samples].sort((a, b) => {
    const dateA = getSampleDateObj(a);
    const dateB = getSampleDateObj(b);
    if (!dateA.isValid() && !dateB.isValid()) return 0;
    if (!dateA.isValid()) return 1;
    if (!dateB.isValid()) return -1;
    return dateB.valueOf() - dateA.valueOf(); // newest to oldest
  });
}

const STLViewerModal = ({ isOpen, onClose, station, formTitle, configId, manualConfigs }: STLViewerModalProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stlUrl, setStlUrl] = useState<string | null>(null);
  const [markerPositions, setMarkerPositions] = useState<MarkerPosition[]>([]);
  const [measurementData, setMeasurementData] = useState<MeasurementData[]>([]);
  const [sampleData, setSampleData] = useState<any[]>([]);
  const [selectedSamples, setSelectedSamples] = useState<string[]>([]);
  const [showSampleSelection, setShowSampleSelection] = useState(false);
  const [selectionMethod, setSelectionMethod] = useState<'count' | 'date' | 'manual'>('count');
  const [sampleCount, setSampleCount] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [partConfigData, setPartConfigData] = useState<any>(null);
  const [nominalData, setNominalData] = useState<any[]>([]);
  const [selectedMarkerId, setSelectedMarkerId] = useState<string | null>(null);
  const [popupPosition, setPopupPosition] = useState<{ x: number; y: number }>({ x: 100, y: 100 });
  const [markerScreenPositions, setMarkerScreenPositions] = useState<Record<string, { x: number; y: number }>>({});
  const [selectedMarkerIds, setSelectedMarkerIds] = useState<string[]>([]);
  const [popupPositions, setPopupPositions] = useState<Record<string, { x: number; y: number }>>({});
  const [shiftSelectedMarkers, setShiftSelectedMarkers] = useState<string[]>([]);
  const [distanceMeasurement, setDistanceMeasurement] = useState<{
    marker1: string;
    marker2: string;
    distance: number;
    lineElement: HTMLElement | null;
  } | null>(null);
  const [showDistancePopup, setShowDistancePopup] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  // Get the config for this form
  const config = manualConfigs.find(c => c.id === configId);

  useEffect(() => {
    if (isOpen && config && user) {
      loadSTLData();
    }
  }, [isOpen, config, user]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStlUrl(null);
      setMarkerPositions([]);
      setMeasurementData([]);
      setSampleData([]);
      setSelectedSamples([]);
      setShowSampleSelection(false);
      setSelectionMethod('count');
      setSampleCount('');
      setDateFrom('');
      setDateTo('');
      setPartConfigData(null);
      setNominalData([]);
      setSelectedMarkerId(null);
      setError(null);
      setShowDistancePopup(false);
    }
  }, [isOpen]);

  // Process measurement data when selected samples change
  useEffect(() => {
    if (sampleData.length > 0 && selectedSamples.length > 0) {
      processMeasurementData();
    }
  }, [sampleData, selectedSamples]);

  // Clean up distance measurement when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Remove distance line and label when modal closes
      const existingLine = document.querySelector('.distance-line');
      const existingLabel = document.querySelector('.distance-label');
      if (existingLine) existingLine.remove();
      if (existingLabel) existingLabel.remove();
      
      // Clear state
      setDistanceMeasurement(null);
      setShiftSelectedMarkers([]);
      setShowDistancePopup(false);
    }
  }, [isOpen]);

  // Clean up distance measurement when selected markers change
  useEffect(() => {
    if (selectedMarkerIds.length === 0) {
      // Remove distance line and label when no markers are selected
      const existingLine = document.querySelector('.distance-line');
      const existingLabel = document.querySelector('.distance-label');
      if (existingLine) existingLine.remove();
      if (existingLabel) existingLabel.remove();
      
      // Clear state
      setDistanceMeasurement(null);
      setShiftSelectedMarkers([]);
      setShowDistancePopup(false);
    }
  }, [selectedMarkerIds]);

  // Clean up distance measurement when shift-selected markers are cleared
  useEffect(() => {
    if (shiftSelectedMarkers.length === 0 && distanceMeasurement) {
      // Remove distance line and label when shift-selected markers are cleared
      const existingLine = document.querySelector('.distance-line');
      const existingLabel = document.querySelector('.distance-label');
      if (existingLine) existingLine.remove();
      if (existingLabel) existingLabel.remove();
      
      // Clear state
      setDistanceMeasurement(null);
      setShowDistancePopup(false);
    }
  }, [shiftSelectedMarkers, distanceMeasurement]);

  const loadSTLData = async () => {
    if (!config?.config_data?.part_configuration_id) {
      setError('No part configuration found for this data collection');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Validate workspace access with fallback logic
      const currentSlug = getCurrentTenantSlug();
      let workspace = currentSlug ? await WorkspaceService.getWorkspaceBySlug(currentSlug) : null;
      
      // If workspace not found by slug, try fallback logic
      if (!workspace) {
        try {
          const userWorkspaces = await WorkspaceService.getUserWorkspaces();
          if (userWorkspaces && userWorkspaces.length > 0) {
            workspace = userWorkspaces.find(ws => ws.is_default) || userWorkspaces[0];
          }
        } catch (error) {
          console.error('Error getting user workspaces for STL loading:', error);
        }
      }
      
      if (!workspace) {
        throw new Error('No workspace found or access denied');
      }
      // 1. Load part configuration (STL file path and marker positions)
      const { data: partConfig, error: partError } = await (supabase as any)
        .from('part_configurations')
        .select('*')
        .eq('id', config.config_data.part_configuration_id)
        .single();

      if (partError) throw partError;

      if (!partConfig) {
        throw new Error('Part configuration not found');
      }

      setPartConfigData(partConfig);

      // 2. Get STL file URL from storage
      if (partConfig.stl_file_path) {
        const { data: urlData } = await supabase.storage
          .from('stl-files')
          .createSignedUrl(partConfig.stl_file_path, 3600); // 1 hour expiry

        if (urlData?.signedUrl) {
          setStlUrl(urlData.signedUrl);
        } else {
          throw new Error('Failed to get STL file URL');
        }
      } else {
        throw new Error('No STL file configured for this part');
      }

      // 3. Extract marker positions from part configuration
      if (partConfig.marker_positions && Array.isArray(partConfig.marker_positions)) {
        const markers: MarkerPosition[] = partConfig.marker_positions.map((marker: any, index: number) => {
          // Use raw coordinates without transform
          const position: [number, number, number] = [
            marker.x || 0,  // Use raw X
            marker.y || 0,  // Use raw Y  
            marker.z || 0   // Use raw Z
          ];
          
          // Comment out the coordinate transform
          // Map coordinates as [X, Z, Y] 
          // const position: [number, number, number] = [
          //   marker.x || 0,  // CAD X → Three.js X
          //   marker.z || 0,  // CAD Z → Three.js Y  
          //   marker.y || 0   // CAD Y → Three.js Z
          // ];
          
          return {
            id: marker.featureLabel || `marker_${index}`,
            featureLabel: marker.featureLabel || `Feature_${index}`,
            position: position
          };
        });
        setMarkerPositions(markers);
      }

      // 4. Load measurement data from sample_data table
      const { data: sampleDataResult, error: sampleError } = await (supabase as any)
        .from('sample_data')
        .select('*')
        .eq('part_configuration_id', config.config_data.part_configuration_id)
        .order('sample_date', { ascending: false });

      if (sampleError) {
        console.error('Error loading sample data:', sampleError);
        // Don't throw error here - STL can still be viewed without measurement data
      } else {
        setSampleData(sampleDataResult || []);
        // Initially select all samples
        const allSampleIds = (sampleDataResult || []).map((sample: any) => 
          sample.metadata?.sample_id || sample.id || `Sample ${sample.sample_date}`
        );
        setSelectedSamples(allSampleIds);
        
        // Auto-populate date range with earliest and latest samples
        if (sampleDataResult && sampleDataResult.length > 0) {
          const dates = sampleDataResult.map((sample: any) => new Date(sample.sample_date));
          const earliestDate = new Date(Math.min(...dates.map(d => d.getTime())));
          const latestDate = new Date(Math.max(...dates.map(d => d.getTime())));
          
          setDateFrom(earliestDate.toISOString().split('T')[0]);
          setDateTo(latestDate.toISOString().split('T')[0]);
          setSampleCount(sampleDataResult.length.toString());
        }

        // 5. Load nominal information from nominal_information table
        const { data: nominalInfo, error: nominalError } = await (supabase as any)
          .from('nominal_information')
          .select('*')
          .eq('part_configuration_id', config.config_data.part_configuration_id);

        if (nominalError) {
          console.error('Error loading nominal information:', nominalError);
          // Don't throw error here - measurements can still work without nominal data
        } else {
          setNominalData(nominalInfo || []);
        }
      }

    } catch (error) {
      console.error('Error loading STL data:', error);
      setError(error instanceof Error ? error.message : 'Failed to load 3D model data');
      toast({
        title: "Error",
        description: "Failed to load 3D model data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMarkerClick = (markerId: string, screenPosition?: { x: number; y: number }, event?: any) => {
    // Check if shift key is pressed
    const isShiftPressed = event?.shiftKey || false;
    
    if (isShiftPressed) {
      // Shift-click: Add to distance measurement selection
      setShiftSelectedMarkers(prev => {
        if (prev.includes(markerId)) {
          // If already selected, remove it
          return prev.filter(id => id !== markerId);
        }
        
        const newSelection = [...prev, markerId];
        
        // If we have exactly 2 markers, calculate distance
        if (newSelection.length === 2) {
          calculateDistanceBetweenMarkers(newSelection[0], newSelection[1]);
        }
        
        return newSelection;
      });
    } else {
      // Normal click: Add marker to selected list if not already selected
      setSelectedMarkerIds(prev => {
        if (prev.includes(markerId)) {
          return prev; // Already selected, don't add again
        }
        return [...prev, markerId];
      });
      
      // Position popup higher up on the canvas
      setPopupPositions(prev => ({
        ...prev,
        [markerId]: { x: 20, y: -650 }
      }));
    }
  };

  const calculateDistanceBetweenMarkers = (marker1Id: string, marker2Id: string) => {
    const marker1 = markerPositions.find(m => m.id === marker1Id);
    const marker2 = markerPositions.find(m => m.id === marker2Id);
    
    if (!marker1 || !marker2) {
      console.error('Markers not found for distance calculation');
      return;
    }
    
    // Calculate 3D distance between markers
    const dx = marker2.position[0] - marker1.position[0];
    const dy = marker2.position[1] - marker1.position[1];
    const dz = marker2.position[2] - marker1.position[2];
    const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
    

    
    // Create line element between markers
    createDistanceLine(marker1Id, marker2Id, distance);
    
    // Store distance measurement
    setDistanceMeasurement({
      marker1: marker1Id,
      marker2: marker2Id,
      distance,
      lineElement: null // Will be set by createDistanceLine
    });
    
    // Show distance measurement popup
    setShowDistancePopup(true);
  };

  // Handle continuous marker position updates
  const handleMarkerPositionUpdate = (markerId: string, screenPosition: { x: number; y: number }) => {
    setMarkerScreenPositions(prev => ({
      ...prev,
      [markerId]: screenPosition
    }));
    
    // Update distance line if we have an active distance measurement
    if (distanceMeasurement && (markerId === distanceMeasurement.marker1 || markerId === distanceMeasurement.marker2)) {
      updateDistanceLine(distanceMeasurement.marker1, distanceMeasurement.marker2, distanceMeasurement.distance);
    }
  };

  const handleClosePopup = (markerId: string) => {
    setSelectedMarkerIds(prev => prev.filter(id => id !== markerId));
    setPopupPositions(prev => {
      const newPositions = { ...prev };
      delete newPositions[markerId];
      return newPositions;
    });
  };

  const getMarkerMeasurements = (markerId: string) => {
    const marker = markerPositions.find(m => m.id === markerId);
    if (!marker) return [];
    
    return measurementData.filter(m => m.feature_label === marker.featureLabel);
  };

  // Utility: calculate tolerance compliance percentage for a marker
  const getMarkerToleranceCompliance = (markerId: string) => {
    const marker = markerPositions.find(m => m.id === markerId);
    if (!marker) return { percentage: 0, color: 'bg-gray-500' };

    // Get all measurements for this marker
    const markerMeasurements = measurementData.filter(m => 
      m.feature_label === marker.featureLabel
    );

    if (markerMeasurements.length === 0) {
      return { percentage: 0, color: 'bg-gray-500' }; // No data
    }

    // Get all nominal data for this marker (across all characteristics)
    const nominalInfos = nominalData.filter(n => 
      n.feature_label === marker.featureLabel
    );

    if (nominalInfos.length === 0) {
      return { percentage: 0, color: 'bg-gray-500' }; // No tolerance data
    }

    // Calculate tolerance compliance across all characteristics
    let totalMeasurements = 0;
    let totalInTolerance = 0;

    nominalInfos.forEach(nominalInfo => {
      if (nominalInfo.upper_tolerance === undefined || nominalInfo.lower_tolerance === undefined) {
        return; // Skip characteristics without tolerance data
      }

      // Get measurements for this specific characteristic
      const characteristicMeasurements = markerMeasurements.filter(m => 
        m.characteristic === nominalInfo.characteristic
      );

      characteristicMeasurements.forEach(measurement => {
        totalMeasurements++;
        
        const deviation = measurement.deviation !== undefined 
          ? measurement.deviation 
          : measurement.actual_value - (nominalInfo.nominal || 0);
        
        if (deviation >= nominalInfo.lower_tolerance && deviation <= nominalInfo.upper_tolerance) {
          totalInTolerance++;
        }
      });
    });

    if (totalMeasurements === 0) {
      return { percentage: 0, color: 'bg-gray-500' }; // No valid measurements
    }

    const percentage = (totalInTolerance / totalMeasurements) * 100;

    // Determine color based on compliance percentage
    let color = 'bg-gray-500'; // Default
    if (percentage >= 95) {
      color = 'bg-green-500'; // Above 95% - Green
    } else if (percentage >= 90) {
      color = 'bg-yellow-500'; // 90-95% - Yellow
    } else if (percentage >= 80) {
      color = 'bg-orange-500'; // 80-90% - Orange
    } else {
      color = 'bg-red-500'; // Less than 80% - Red
    }

    return { percentage, color };
  };

  const handleClose = () => {
    onClose();
  };

  const handleEscapeKey = () => {
    // If there are open measurement popups, clear them instead of closing the modal
    if (selectedMarkerIds.length > 0) {
      setSelectedMarkerIds([]);
      setPopupPositions({});
      return;
    }
    
    // If distance measurement popup is open, close it instead of closing the modal
    if (showDistancePopup) {
      handleCloseDistancePopup();
      return;
    }
    
    // If no popups are open, close the modal
    onClose();
  };

  const processMeasurementData = () => {
    const processedData: MeasurementData[] = [];
    
    sampleData.forEach((sample: any) => {
      const sampleId = sample.metadata?.sample_id || sample.id || `Sample ${sample.sample_date}`;
      
      // Only process samples that are selected
      if (selectedSamples.includes(sampleId) && sample.measurements && Array.isArray(sample.measurements)) {
        sample.measurements.forEach((measurement: any) => {
          processedData.push({
            feature_label: measurement.feature_label,
            characteristic: measurement.characteristic,
            actual_value: measurement.actual_value,
            deviation: measurement.deviation,
            nominal_value: measurement.nominal_value,
            upper_tolerance: measurement.upper_tolerance,
            lower_tolerance: measurement.lower_tolerance,
            sample_date: sample.sample_date,
            sample_id: sampleId,
            metadata: sample.metadata || {} // Attach parent metadata
          });
        });
      }
    });

    setMeasurementData(processedData);
  };

  // Helper functions for sample selection
  const validateSampleCount = (count: string): boolean => {
    const num = parseInt(count, 10);
    return !isNaN(num) && num > 0 && num <= sampleData.length;
  };

  const getFilteredSamples = () => {
    if (selectionMethod === 'count') {
      if (!validateSampleCount(sampleCount)) return [];
      const count = parseInt(sampleCount, 10);
      // Get newest to oldest, then select the newest X samples (from the beginning)
      const sortedSamples = getChronologicalSamples(sampleData);
      return sortedSamples.slice(0, count); // first X samples (newest)
    } else if (selectionMethod === 'date') {
      if (!dateFrom || !dateTo) return [];
      const fromDate = new Date(dateFrom);
      const toDate = new Date(dateTo);
      // Filter by date range and sort oldest to newest
      const filtered = getChronologicalSamples(sampleData).filter(sample => {
        const sampleDate = getSampleDateObj(sample);
        return sampleDate.isValid() && sampleDate.isAfter(dayjs(fromDate).subtract(1, 'day')) && sampleDate.isBefore(dayjs(toDate).add(1, 'day'));
      });
      return filtered;
    } else {
      // Manual selection - filter selected samples and sort oldest to newest
      const filtered = getChronologicalSamples(sampleData).filter(sample => {
        const sampleId = sample.metadata?.sample_id || sample.id || `Sample ${sample.sample_date}`;
        return selectedSamples.includes(sampleId);
      });
      return filtered;
    }
  };

  const applySelection = () => {
    const filtered = getFilteredSamples();
    const filteredIds = filtered.map(sample => 
      sample.metadata?.sample_id || sample.id || `Sample ${sample.sample_date}`
    );
    setSelectedSamples(filteredIds);
    setShowSampleSelection(false);
    processMeasurementData();
  };

  const handleSampleToggle = (sampleId: string) => {
    setSelectedSamples(prev => 
      prev.includes(sampleId) 
        ? prev.filter(id => id !== sampleId)
        : [...prev, sampleId]
    );
  };

  const selectAllSamples = () => {
    const allIds = sampleData.map(sample => 
      sample.metadata?.sample_id || sample.id || `Sample ${sample.sample_date}`
    );
    setSelectedSamples(allIds);
  };

  const deselectAllSamples = () => {
    setSelectedSamples([]);
  };

  const createDistanceLine = (marker1Id: string, marker2Id: string, distance: number) => {
    const container = document.getElementById('stl-viewer-container');
    if (!container) return;
    
    // Remove existing distance line if any
    const existingLine = document.querySelector('.distance-line');
    if (existingLine) {
      existingLine.remove();
    }
    
    // Get marker screen positions
    const marker1ScreenPos = markerScreenPositions[marker1Id];
    const marker2ScreenPos = markerScreenPositions[marker2Id];
    
    if (!marker1ScreenPos || !marker2ScreenPos) {
      console.error('Marker screen positions not available for line creation');
      return;
    }
    
    // Create line element
    const lineDiv = document.createElement('div');
    lineDiv.className = 'distance-line';
    lineDiv.style.position = 'absolute';
    lineDiv.style.backgroundColor = '#00ff00'; // Green color for distance line
    lineDiv.style.height = '3px';
    lineDiv.style.boxShadow = '0 0 3px #00ff00';
    lineDiv.style.transformOrigin = '0 50%';
    lineDiv.style.pointerEvents = 'none';
    lineDiv.style.zIndex = '5';
    lineDiv.style.opacity = '1';
    
    // Calculate line position and angle
    const containerRect = container.getBoundingClientRect();
    const marker1X = marker1ScreenPos.x - containerRect.left;
    const marker1Y = marker1ScreenPos.y - containerRect.top;
    const marker2X = marker2ScreenPos.x - containerRect.left;
    const marker2Y = marker2ScreenPos.y - containerRect.top;
    
    const lineDistance = Math.sqrt(Math.pow(marker2X - marker1X, 2) + Math.pow(marker2Y - marker1Y, 2));
    const angle = Math.atan2(marker2Y - marker1Y, marker2X - marker1X) * 180 / Math.PI;
    
    // Position the line
    lineDiv.style.left = marker1X + 'px';
    lineDiv.style.top = marker1Y + 'px';
    lineDiv.style.width = lineDistance + 'px';
    lineDiv.style.transform = `rotate(${angle}deg)`;
    
    // Create distance label
    const labelDiv = document.createElement('div');
    labelDiv.className = 'distance-label';
    labelDiv.style.position = 'absolute';
    labelDiv.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
    labelDiv.style.color = 'white';
    labelDiv.style.padding = '4px 8px';
    labelDiv.style.borderRadius = '4px';
    labelDiv.style.fontSize = '12px';
    labelDiv.style.fontWeight = 'bold';
    labelDiv.style.pointerEvents = 'none';
    labelDiv.style.zIndex = '6';
    labelDiv.style.transform = 'translate(-50%, -50%)';
    
    // Position label at midpoint of line
    const midX = (marker1X + marker2X) / 2;
    const midY = (marker1Y + marker2Y) / 2;
    labelDiv.style.left = midX + 'px';
    labelDiv.style.top = midY + 'px';
    labelDiv.textContent = `${distance.toFixed(3)} mm`;
    
    // Append elements to container
    container.appendChild(lineDiv);
    container.appendChild(labelDiv);
    
  };

  const updateDistanceLine = (marker1Id: string, marker2Id: string, distance: number) => {
    const container = document.getElementById('stl-viewer-container');
    if (!container) return;
    
    // Get current marker screen positions
    const marker1ScreenPos = markerScreenPositions[marker1Id];
    const marker2ScreenPos = markerScreenPositions[marker2Id];
    
    if (!marker1ScreenPos || !marker2ScreenPos) {
      console.error('Marker screen positions not available for line update');
      return;
    }
    
    // Find existing line and label
    const existingLine = document.querySelector('.distance-line') as HTMLElement;
    const existingLabel = document.querySelector('.distance-label') as HTMLElement;
    
    if (!existingLine || !existingLabel) {
      console.error('Distance line elements not found for update');
      return;
    }
    
    // Calculate new line position and angle
    const containerRect = container.getBoundingClientRect();
    const marker1X = marker1ScreenPos.x - containerRect.left;
    const marker1Y = marker1ScreenPos.y - containerRect.top;
    const marker2X = marker2ScreenPos.x - containerRect.left;
    const marker2Y = marker2ScreenPos.y - containerRect.top;
    
    const lineDistance = Math.sqrt(Math.pow(marker2X - marker1X, 2) + Math.pow(marker2Y - marker1Y, 2));
    const angle = Math.atan2(marker2Y - marker1Y, marker2X - marker1X) * 180 / Math.PI;
    
    // Update line position and dimensions
    existingLine.style.left = marker1X + 'px';
    existingLine.style.top = marker1Y + 'px';
    existingLine.style.width = lineDistance + 'px';
    existingLine.style.transform = `rotate(${angle}deg)`;
    
    // Update label position (midpoint of line)
    const midX = (marker1X + marker2X) / 2;
    const midY = (marker1Y + marker2Y) / 2;
    existingLabel.style.left = midX + 'px';
    existingLabel.style.top = midY + 'px';
    

  };

  const handleCloseDistancePopup = () => {
    setShowDistancePopup(false);
    // Clear the distance measurement and line
    const existingLine = document.querySelector('.distance-line');
    const existingLabel = document.querySelector('.distance-label');
    if (existingLine) existingLine.remove();
    if (existingLabel) existingLabel.remove();
    setDistanceMeasurement(null);
    setShiftSelectedMarkers([]);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleEscapeKey}>
      <DialogContent className="max-w-[calc(100vw-20px)] max-h-[calc(100vh-20px)] overflow-hidden p-3">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Box className="w-5 h-5" />
            3D Model Viewer - {formTitle}
          </DialogTitle>
          <DialogDescription>
            Interactive 3D visualization for {station?.name}
          </DialogDescription>
          {partConfigData && (
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="outline">{partConfigData.part_number}</Badge>
              <Badge variant="secondary">{markerPositions.length} markers</Badge>
              <Badge variant="secondary">{sampleData.length} samples</Badge>
              {sampleData.length > 0 && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowSampleSelection(true)}
                  className="ml-2"
                >
                  <Filter className="w-4 h-4 mr-1" />
                  Select Samples ({selectedSamples.length})
                </Button>
              )}
            </div>
          )}
        </DialogHeader>
        
        <div className="flex-1 h-[calc(100vh-200px)] relative overflow-hidden" id="stl-viewer-container">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm z-10">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Loading 3D model...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm z-10">
              <div className="text-center max-w-md">
                <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Failed to Load 3D Model</h3>
                <p className="text-sm text-muted-foreground mb-4">{error}</p>
                <div className="space-y-2">
                  <Button onClick={loadSTLData} variant="outline" size="sm">
                    Try Again
                  </Button>
                  <Button onClick={handleClose} variant="ghost" size="sm">
                    Close
                  </Button>
                </div>
              </div>
            </div>
          )}

          {stlUrl && !loading && !error && (
            <Interactive3DViewer
              stlUrl={stlUrl}
              markerPositions={markerPositions.map(marker => {
                const { color } = getMarkerToleranceCompliance(marker.id);
                // Convert Tailwind color classes to CSS colors
                const getColorValue = (colorClass: string) => {
                  switch (colorClass) {
                    case 'bg-green-500': return '#10b981';
                    case 'bg-yellow-500': return '#eab308';
                    case 'bg-orange-500': return '#f97316';
                    case 'bg-red-500': return '#ef4444';
                    case 'bg-blue-500': return '#3b82f6';
                    default: return '#6b7280'; // gray-500
                  }
                };
                return {
                  ...marker,
                  color: getColorValue(color)
                };
              })}
              onMarkerClick={handleMarkerClick}
              onMarkerPositionUpdate={handleMarkerPositionUpdate}
              activeMarkerId={selectedMarkerIds.length > 0 ? selectedMarkerIds[selectedMarkerIds.length - 1] : undefined}
              className="w-full h-full"
            />
          )}

          {!stlUrl && !loading && !error && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <Eye className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No 3D Model Available</h3>
                <p className="text-sm text-muted-foreground">
                  This configuration doesn't have a 3D model set up yet.
                </p>
              </div>
            </div>
          )}

          {/* Measurement Popups - positioned absolutely over the canvas */}
          {selectedMarkerIds.map(markerId => {
            const marker = markerPositions.find(m => m.id === markerId);
            return (
              <MeasurementPopup
                key={markerId}
                markerId={markerId}
                featureLabel={marker?.featureLabel || markerId}
                measurements={getMarkerMeasurements(markerId)}
                nominalData={nominalData}
                onClose={() => handleClosePopup(markerId)}
                position={popupPositions[markerId] || { x: 100, y: 100 }}
                markerScreenPosition={markerScreenPositions[markerId]}
                containerId="stl-viewer-container"
              />
            );
          })}

          {/* Distance Measurement Popup */}
          {showDistancePopup && distanceMeasurement && (
            <DistanceMeasurementPopup
              marker1Id={distanceMeasurement.marker1}
              marker2Id={distanceMeasurement.marker2}
              marker1Label={markerPositions.find(m => m.id === distanceMeasurement.marker1)?.featureLabel || distanceMeasurement.marker1}
              marker2Label={markerPositions.find(m => m.id === distanceMeasurement.marker2)?.featureLabel || distanceMeasurement.marker2}
              distance={distanceMeasurement.distance}
              onClose={handleCloseDistancePopup}
              position={{ x: 200, y: -650 }}
              measurementData={measurementData}
              nominalData={nominalData}
              containerId="stl-viewer-container"
            />
          )}
        </div>
      </DialogContent>

      {/* Enhanced Sample Selection Modal */}
      <Dialog open={showSampleSelection} onOpenChange={setShowSampleSelection}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Select Samples to Display
            </DialogTitle>
            <DialogDescription>
              Choose which samples to include in the 3D visualization and analysis
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto">
            <Tabs value={selectionMethod} onValueChange={(value) => setSelectionMethod(value as 'count' | 'date' | 'manual')}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="count" className="flex items-center gap-2">
                  <Hash className="h-4 w-4" />
                  By Count
                </TabsTrigger>
                <TabsTrigger value="date" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  By Date Range
                </TabsTrigger>
                <TabsTrigger value="manual" className="flex items-center gap-2">
                  <List className="h-4 w-4" />
                  Manual Selection
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="count" className="space-y-4 mt-6">
                <div className="space-y-2">
                  <Label htmlFor="sample-count">Number of samples (latest X samples)</Label>
                  <Input
                    id="sample-count"
                    type="number"
                    placeholder={`Enter number (1-${sampleData.length})`}
                    value={sampleCount}
                    onChange={(e) => setSampleCount(e.target.value)}
                    min="1"
                    max={sampleData.length}
                  />
                  {sampleCount && !validateSampleCount(sampleCount) && (
                    <p className="text-sm text-red-600">
                      Please enter a valid number between 1 and {sampleData.length}
                    </p>
                  )}
                  {sampleCount && validateSampleCount(sampleCount) && (
                    <p className="text-sm text-green-600">
                      Will select the latest {sampleCount} samples
                    </p>
                  )}
                </div>
              </TabsContent>
              
              <TabsContent value="date" className="space-y-4 mt-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date-from">Date from:</Label>
                    <Input
                      id="date-from"
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="date-to">Date to:</Label>
                    <Input
                      id="date-to"
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                    />
                  </div>
                </div>
                {dateFrom && dateTo && (
                  <p className="text-sm text-green-600">
                    Will select {getFilteredSamples().length} samples from {new Date(dateFrom).toLocaleDateString()} to {new Date(dateTo).toLocaleDateString()}
                  </p>
                )}
              </TabsContent>
              
              <TabsContent value="manual" className="space-y-4 mt-6">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">
                    Select individual samples ({selectedSamples.length} of {sampleData.length} selected) - sorted by date/time (newest first)
                  </span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={selectAllSamples}>
                      Select All
                    </Button>
                    <Button variant="outline" size="sm" onClick={deselectAllSamples}>
                      Clear All
                    </Button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto border rounded p-3">
                  {getChronologicalSamples(sampleData).map((sample, index) => {
                    const sampleId = sample.metadata?.sample_id || sample.id || `Sample ${sample.sample_date}`;
                    const isSelected = selectedSamples.includes(sampleId);
                    const dateObj = getSampleDateObj(sample);
                    const displayDate = dateObj.isValid() ? dateObj.format('YYYY-MM-DD HH:mm:ss') : 'Invalid date';
                    const measurementCount = sample.measurements ? sample.measurements.length : 0;
                    

                    
                    return (
                      <div key={index} className="flex items-center justify-between p-2 hover:bg-gray-100 rounded">
                        <div className="flex items-center space-x-3">
                          <Checkbox
                            id={`sample-${index}`}
                            checked={isSelected}
                            onCheckedChange={() => handleSampleToggle(sampleId)}
                          />
                          <div className="flex-1">
                            <div className="font-medium text-sm">{sampleId}</div>
                            <div className="text-xs text-gray-500">{displayDate}</div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Badge variant="outline" className="text-xs">
                            {measurementCount} measurements
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </TabsContent>
            </Tabs>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => setShowSampleSelection(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={applySelection}
              disabled={
                (selectionMethod === 'count' && !validateSampleCount(sampleCount)) ||
                (selectionMethod === 'date' && (!dateFrom || !dateTo)) ||
                (selectionMethod === 'manual' && selectedSamples.length === 0)
              }
            >
              Apply Selection
              {selectionMethod === 'count' && validateSampleCount(sampleCount) && ` (${sampleCount} samples)`}
              {selectionMethod === 'date' && dateFrom && dateTo && ` (${getFilteredSamples().length} samples)`}
              {selectionMethod === 'manual' && ` (${selectedSamples.length} samples)`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default STLViewerModal; 